import React from 'react';
import { RadioGroup as ChakraRadio, useRadioGroupContext } from '@chakra-ui/react';
import { CoreRadioProps } from './types';
import { OptionLabel } from '../OptionLabel';

// RadioGroup.Item needs a RadioGroup.Root ancestor for context, so a lone radio self-provides one. The hook
// always runs in the same position (only whether it throws varies), so the rules of hooks hold.
const useHasAmbientRadioGroup = (): boolean => {
  try {
    useRadioGroupContext();
    return true;
  } catch {
    return false;
  }
};

export const Radio = React.forwardRef<HTMLDivElement, CoreRadioProps>(
  (
    {
      children,
      label,
      helpText,
      tooltip,
      onChange,
      isDisabled,
      disabled,
      isInvalid,
      invalid,
      isChecked,
      checked,
      value,
      name,
      ...rest
    }: CoreRadioProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const hasAmbientRadioGroup = useHasAmbientRadioGroup();
    const itemValue = value ?? '';
    // An item is checked only when its `value` matches the Root's; this per-item checked/isChecked applies only
    // to the self-provided Root below, since a caller's own Root owns checked state.
    const effectiveChecked = isChecked ?? checked;

    // `onChange` goes on ItemHiddenInput, as with Checkbox's HiddenInput: Item (the outer <label>) has no change
    // callback, so a spread-only onChange never fires.
    const radioItem = (
      <ChakraRadio.Item
        ref={ref as never}
        value={itemValue}
        disabled={isDisabled ?? disabled}
        invalid={isInvalid ?? invalid}
        {...rest}
      >
        <ChakraRadio.ItemHiddenInput onChange={onChange} />
        <ChakraRadio.ItemIndicator />
        <ChakraRadio.ItemText>
          <OptionLabel label={label} helpText={helpText} tooltip={tooltip}>
            {children}
          </OptionLabel>
        </ChakraRadio.ItemText>
      </ChakraRadio.Item>
    );

    if (hasAmbientRadioGroup) {
      return radioItem;
    }

    // `readOnly` stays unwired: Item has none, and on this self-provided Root it breaks controlled `value` sync
    // on rerender (cause unknown). A caller's `name` must win over Root's own useId() fallback.
    return (
      <ChakraRadio.Root name={name} value={effectiveChecked ? itemValue : null}>
        {radioItem}
      </ChakraRadio.Root>
    );
  }
);
Radio.displayName = 'radio';
